import { useCallback, useMemo } from 'react';

import { useGetBuyerEntityChatsQuery } from '../../../../../generated';
import { BuyerChat, InvalidBuyerChat } from '../types';

interface UseGetBuyerChatsProps {
  buyerId: string;
  userId: string;
}

interface UseGetBuyerChatsReturn {
  chats: BuyerChat[];
  invalidChats: InvalidBuyerChat[];
  loading: boolean;
  error?: Error;
  fetchMore: () => void;
}

export const useGetBuyerChats = ({
  buyerId,
  userId,
}: UseGetBuyerChatsProps): UseGetBuyerChatsReturn => {
  const { data, loading, error, fetchMore } = useGetBuyerEntityChatsQuery({
    variables: {
      buyerEntityId: buyerId,
      userId,
      offset: 0,
      filter: {
        chat: { enabled: true },
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const formatChatsResponse = useMemo(() => {
    const chats: BuyerChat[] = [];
    const invalidChats: InvalidBuyerChat[] = [];

    data?.search.hits.forEach((hit) => {
      const { entity } = hit;
      if (!entity || entity.__typename !== 'Chat') return;

      if (entity.supplier) {
        chats.push({
          id: entity.id,
          name: entity.chatName || '',
          supplierId: entity.supplier.id,
          supplierName: entity.supplier.name || null,
          isUserInChat: entity.isUserInChat || false,
        });
      } else {
        invalidChats.push({
          chatId: entity.id,
          chatName: entity.chatName,
        });
      }
    });

    return { chats, invalidChats };
  }, [data, userId]);

  const handleFetchMore = useCallback(() => {
    const loadedChats =
      formatChatsResponse.chats.length +
      formatChatsResponse.invalidChats.length;
    const total = data?.search.total || 0;

    if (loadedChats >= total) return;

    fetchMore({
      variables: {
        offset: loadedChats,
      },
    });
  }, [data?.search.total, formatChatsResponse, fetchMore]);

  return {
    chats: formatChatsResponse.chats,
    invalidChats: formatChatsResponse.invalidChats,
    loading,
    error: error as Error | undefined,
    fetchMore: handleFetchMore,
  };
};
